import { useEffect, useState } from "react";
import {
  HashRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Guide from "pages/Guide";
import Privacy from "pages/policy/Privacy";
import Service from "pages/policy/Service";
import Benefit from "pages/event/Benefit";
import Comment from "components/molecules/Comment";
import ErrorFallback from "pages/Error";
import { ErrorBoundary } from "react-error-boundary";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import Operation from "pages/policy/Operation";
import Health from "pages/policy/Health";
import Place from "pages/policy/Place";
import StudioPrivacy from "pages/policy/StudioPrivacy";
import PremiumService from "pages/policy/PremiumService";
import PremiumPrivacy from "pages/policy/PremiumPrivacy";
import StudioWeeks21Privacy from "pages/policy/StudioWeeks21Privacy";
import InboundPrivacy from "pages/policy/InboundPrivacy";
import MomsClassPrivacy from "pages/policy/MomsClassPrivacy";
import EnService from "pages/policy/EnService";
import JaService from "pages/policy/JaService";
import EnPrivacy from "pages/policy/EnPrivacy";
import JaPrivacy from "pages/policy/JaPrivacy";
import KidsShare from "pages/event/KidsShare";
import { useAuthStore } from "stores/authStore";
import Loading from "components/atoms/Loading";

const withErrorBoundary = (Component: JSX.Element) => (
  <QueryErrorResetBoundary>
    <ErrorBoundary FallbackComponent={ErrorFallback}>{Component}</ErrorBoundary>
  </QueryErrorResetBoundary>
);

// 인증이 필요한 라우트는 AuthOutlet으로 감싸서 토큰이 없는 경우 접근 차단
const AuthOutlet = () => {
  const token = useAuthStore((state) => state.token);
  const isWebView =
    typeof window !== "undefined" && !!window.ReactNativeWebView;
  const waitForToken = isWebView && !token;

  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    if (!waitForToken) return;
    const timer = setTimeout(() => setIsTimeout(true), 5000);
    return () => clearTimeout(timer);
  }, [waitForToken]);

  if (isTimeout)
    return <ErrorFallback error={undefined} resetErrorBoundary={() => {}} />;
  if (waitForToken) return <Loading />;
  return <Outlet />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const Router = () => {
  return (
    // 아파치 설정으로 인해 BrowserRouter 사용하면 안됨
    <HashRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Guide />} />
        <Route path="/policy/service" element={<Service />} />
        <Route path="/policy/privacy" element={<Privacy />} />
        <Route path="/policy/operation" element={<Operation />} />
        <Route path="/policy/health" element={<Health />} />
        <Route path="/policy/place" element={<Place />} />
        <Route path="/policy/studioPrivacy" element={<StudioPrivacy />} />
        <Route path="/policy/premiumService" element={<PremiumService />} />
        <Route path="/policy/premiumPrivacy" element={<PremiumPrivacy />} />
        <Route
          path="/policy/studioWeeks21Privacy"
          element={<StudioWeeks21Privacy />}
        />
        <Route path="/policy/inboundPrivacy" element={<InboundPrivacy />} />
        <Route path="/policy/momsClassPrivacy" element={<MomsClassPrivacy />} />
        <Route path="/policy/en/service" element={<EnService />} />
        <Route path="/policy/en/privacy" element={<EnPrivacy />} />
        <Route path="/policy/ja/service" element={<JaService />} />
        <Route path="/policy/ja/privacy" element={<JaPrivacy />} />

        <Route
          path="/error"
          element={
            <ErrorFallback error={undefined} resetErrorBoundary={() => {}} />
          }
        />

        {/* 토큰이 필요한 라우트 */}
        <Route element={<AuthOutlet />}>
          <Route
            path="/common/comment"
            element={withErrorBoundary(<Comment svcId={29} />)}
          />
          <Route
            path="/event/benefit"
            element={withErrorBoundary(<Benefit />)}
          />
          <Route
            path="/event/kidsShare"
            element={withErrorBoundary(<KidsShare />)}
          />
        </Route>
      </Routes>
    </HashRouter>
  );
};
